<template>
  <div class="view-wrapper">
    <ViewHeader title="Initiatives" :isFullViewport="true" :hideSectionHomeButton="true" :hideRoleTutorial="true">
      <template #actions>
        <Button @click="() => {
          $router.back()
        }" label="Close" class="p-button-outlined p-ml-2" />
      </template>
    </ViewHeader>
    <ViewMain :isFullViewport="true">
      <div class="p-p-4">
        <div class="intro-container">
          <div>
            <p class="plan-settings-instructions">
              Initiatives are strategic efforts that have a specific duration and include multiple connected Tactics.
              They
              are categorized as campaigns, promotions, key dates, or monthly themes.<br /><br />
              Enter Initiatives, then connect Tactics, and use Filters to create isolated views for cross-channel
              optimization. View <a class="text-link" target="_blank"
                href="https://www.annumplanning.com/resources/guides/plan-initiatives/">detailed instructions</a>.
            </p>
          </div>
          <HowItWorks class="p-mb-6" imagePath="how-it-works-initiatives.jpg"
            slideshowPath="https://docs.google.com/presentation/d/e/2PACX-1vSQZBPeymCp6k9IOUmg_EwZk_GqZ0IkvWgpA6heN5ZEDJ0WmFBFgQmR2ZCz3dlXZHK7uXAK2u1rD8O3/embed?start=false&loop=true&delayms=5000"
            :hideOverlay="true" />
        </div>
        <div class="p-d-flex p-ai-end">
          <div class="date-range-container p-mb-3">
            <div class="range p-d-flex">
              <div class="p-field p-mr-2">
                <label for="startDate">Start Date</label>
                <Calendar id="startDate" v-model="dateRangeStart" :manualInput="false" :monthNavigator="true"
                  :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange" :showIcon="true"
                  selectionMode="single" dateFormat="mm/dd/yy" />
              </div>
              <div class="p-field">
                <label for="endDate">End Date</label>
                <Calendar id="endDate" v-model="dateRangeEnd" :manualInput="false" :monthNavigator="true"
                  :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange" :showIcon="true"
                  selectionMode="single" dateFormat="mm/dd/yy" />
              </div>
            </div>
          </div>
          <div class="p-field p-ml-2 p-mb-5">
            <label for="channels">Category</label>
            <Dropdown id="channels" v-model="selectedCategory" :options="categoryOptions" optionLabel="name"
              dataKey="key" :showClear="selectedCategory && selectedCategory.name ? true : false"
              placeholder="Choose Category" @change="() => {
                // Clear selected sort when clearing selection, sort by start date after making selection
                if (selectedCategory && selectedCategory.name) {
                  currentInitiativesTableSortField = 'startDate'
                } else {
                  currentInitiativesTableSortField = ''
                }
              }" />
          </div>
          <div class="p-d-flex p-ai-center p-ml-auto p-mb-5">
            <p class="plan-settings-export-message p-mr-3">The Category filter is not applied to CSV exports.</p>
            <Button label="CSV Export" icon="pi pi-download" class="p-button-outlined"
              :loading="isExportingInitiatives"
              :disabled="isExportingInitiatives || !dateRangeStart || !dateRangeEnd"
              @click="onClickExportInitiatives" />
            <Button v-if="canUserManagePlanUsersChannelsSubscriptions" label="Add Initiative" icon="pi pi-plus-circle"
              class="p-ml-2" @click="handleAddInitiative"
              :disabled="!canUserManagePlanUsersChannelsSubscriptions" />
          </div>
        </div>

        <DataTable 
          v-model="filteredInitiatives" 
          :autoLayout="true" 
          class="initiatives-table" 
          :sortField="currentInitiativesTableSortField" 
          :sortOrder="1" 
          removableSort
          >
          <Column field="title" header="Name" headerClass="title-column" bodyClass="title-column" sortable>
            <template #body="slotProps">
              <span :class="{
                'is-nested': slotProps.data.isNested,
                'is-lead': slotProps.data.isLead,
              }">
                <i v-if="slotProps.data.isInitiativeCustomization" class="pi pi-link"
                  style="font-size: 1.5rem; margin-right: 0.5rem;"></i>
                {{ getTitleForPlanningItem(slotProps.data) }}
              </span>
              <template v-if="$store.getters.isCurrentUserSuperAdmin">
                <span class="id-value"> ({{ slotProps.data.id.intID }})</span>
              </template>
            </template>
          </Column>
          <Column field="initiativeTypeName" header="Category">
            <template #body="slotProps">
              {{ slotProps.data.initiativeTypeName }}
            </template>
          </Column>
          <Column field="startDate" header="Start Date" sortable>
            <template #body="slotProps">
              {{ slotProps.data.startDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column field="endDate" header="End Date" sortable>
            <template #body="slotProps">
              {{ slotProps.data.endDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column header="Marketing Objective" v-if="doesPlanHaveObjectives">
            <template #body="slotProps">
              <template v-if="getObjectivesForPlanningItem(slotProps.data).length">
                <template v-for="(tag, index) in getObjectivesForPlanningItem(slotProps.data)">
                  <span class="tag-title" :key="index">
                    {{ tag.title }}
                    <template v-if="index + 1 < getObjectivesForPlanningItem(slotProps.data).length">
                      &nbsp;/&nbsp;
                    </template>
                  </span>
                </template>
              </template>
            </template>
          </Column>
          <Column header="Audience" v-if="doesPlanHaveTargetSegments">
            <template #body="slotProps">
              <template v-if="getTargetSegmentsForPlanningItem(slotProps.data).length">
                <template v-for="(tag, index) in getTargetSegmentsForPlanningItem(
                  slotProps.data
                )">
                  <span class="tag-title" :key="index">
                    {{ tag.title }}
                    <template v-if="index + 1 < getTargetSegmentsForPlanningItem(slotProps.data).length">
                      &nbsp;/&nbsp;
                    </template>
                  </span>
                </template>
              </template>
            </template>
          </Column>
          <Column header="Notes" key="summary">
            <template #body="slotProps">
              <Button v-if="slotProps.data.summary !== null && slotProps.data.summary !== ''" icon="pi pi-eye"
                class="p-button-icon-only p-button-rounded p-button-text" @mouseenter="(event) => {
                  currentOverlayHTML = slotProps.data.summary ? slotProps.data.summary : ''
                  // @ts-expect-error
                  $refs.richTextFieldOverlay.show(event)
                }" @mouseleave="(event) => {
                  // @ts-expect-error
                  $refs.richTextFieldOverlay.hide()
                }"></Button>
            </template>
          </Column>
          <Column header="Documents" key="documents">
            <template #body="slotProps">
              <div class="p-d-flex p-jc-center">
                <span v-if="slotProps.data.documents.length" class="pi pi-paperclip"></span>
              </div>
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <!-- Initiatives can always be opened (in read-only mode when viewed across plans).
                Nested initiative variants keep the same-plan-context restriction. -->
              <div class="p-d-flex p-jc-end" v-if="
                !slotProps.data.isInitiativeCustomization ||
                (isLeadPlan && slotProps.data.isLead) ||
                (!isLeadPlan && !slotProps.data.isLead)
              ">
                <Button label="Open" icon="pi pi-arrow-circle-up" class="p-button-text"
                  @click="showPlanningItem(slotProps.data)"></Button>
              </div>
            </template>
          </Column>
          <template #empty>
            <h3>No initiatives found for the given date range and category.</h3>
          </template>
        </DataTable>

        <div class="categories-container" style="max-width: 60rem; margin: 0 auto;">
          <DataTable :editingRows.sync="categoryEditingRows" :value="categoryOptions" editMode="row" dataKey="key"
            :autoLayout="true" class="categories-table" @row-edit-init="onCategotyRowEditInit"
            @row-edit-save="onCategotyRowEditSave" @row-reorder="onCategoryRowReorder">
            <Header>
              <h2>Categories of Initiatives</h2>
            </Header>
            <Column rowReorder headerStyle="width: 3rem" v-if="canUserEditPlanProperties && !currentPlan.isNested" />
            <Column field="name" bodyStyle="width: 80%;">
              <template #body="slotProps">
                <div class="p-d-flex p-jc-between p-ai-center">
                  <div>
                    {{ slotProps.data.name }}
                    <template v-if="$store.getters.isCurrentUserSuperAdmin">
                      <span class="id-value"> ({{ slotProps.data.id.intID }})</span>
                    </template>
                  </div>
                  <div v-if="slotProps.data.name.toLowerCase() === initiativeTypeNameMonthlyTheme"
                    style="font-size: 0.9rem;">
                    Duration by Month only
                  </div>
                </div>
              </template>
              <template #editor="slotProps">
                <div class="p-d-flex p-jc-between p-ai-center">
                  <InputText v-model="slotProps.data.name" />
                  <div v-if="slotProps.data.name.toLowerCase() === initiativeTypeNameMonthlyTheme"
                    style="font-size: 0.9rem;">
                    Duration by Month only
                  </div>
                </div>
              </template>
            </Column>
            <Column :rowEditor="true" bodyStyle="text-align: right; width: 10%;"
              v-if="canUserEditPlanProperties && !currentPlan.isNested"></Column>
            <Column bodyStyle="text-align: left; width: 10%;" v-if="canUserEditPlanProperties && !currentPlan.isNested">
              <template #body="slotProps">
                <Button icon="pi pi-trash" severity="danger" class="p-button-text p-button-danger"
                  @click="onCategoryDelete($event, slotProps.data)" />
              </template>
              <template #editor>
                <Button icon="pi pi-trash" severity="danger" class="p-button-text p-button-danger" disabled />
              </template>
            </Column>
          </DataTable>

          <div class="add-initiative-category-container" v-if="canUserEditPlanProperties && !currentPlan.isNested">
            <div class="p-field">
              <label for="add-initiative-category-input">New Category Name </label>
              <InputText v-model="newCategoryName" />
            </div>
            <Button label="New Initiative Category" icon="pi pi-plus-circle" class="p-button-text"
              @click="addCategory()" />
          </div>

          <p v-if="currentPlan.isLead || currentPlan.isNested" style="margin: 2rem auto; text-align: center;">Categories of Initiatives can only be edited within the Lead Plan.</p>
        </div>

        <!-- Display data to super user for use in integration platforms -->
        <div v-if="$store.getters.isCurrentUserSuperAdmin" class="p-mt-4">
          <h2>Integration Data</h2>
          <h3>Account: {{ $store.getters.currentAccount.contactCompanyName }} - {{ $store.getters.currentAccount.id.intID }}</h3>
          <h3>Plan: {{ $store.getters.currentPlan.name }} - {{ $store.getters.currentPlan.id.intID }}</h3>
          <table class="integration-data-table" cellspacing="0">
            <tr>
              <th>Annum InitiativeType ID</th>
              <th>Annum Category</th>
              <th>Annum Plan</th>
            </tr>
            <template v-for="(category) in categoryOptions">
              <tr :key="category.key">
                <td>{{ category.id.intID }}</td>
                <td>{{ category.name }}</td>
                <td>
                  {{ currentPlan.id.intID }}
                  <template v-if="category.abbreviatedPlanName"> - {{ category.abbreviatedPlanName }}</template>
                </td>
              </tr>
            </template>
          </table>
        </div>

      </div>

      <OverlayPanel ref="richTextFieldOverlay" class="rich-text-field-overlay" appendTo="body">
        <div class="notes-field-content-container" v-html="currentOverlayHTML"></div>
      </OverlayPanel>
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import Plan from '@/models/Plan'
import Role from '@/models/Role'
import Channel from '@/models/Channel'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Tactic from '@/models/Tactic'
import TacticType from '@/models/TacticType'
import HowItWorks from '@/components/HowItWorks.vue'
import Tag from '@/models/Tag'
import InitiativeType from '@/models/InitiativeType'
import InitiativeChannel from '@/models/InitiativeChannel'
import Initiative from '@/models/Initiative'
import InitiativeCustomization from '@/models/InitiativeCustomization'
import PlanningItem from '@/models/InterfacePlanningItem'
import ID from '@/models/ID'
import type { PlanExportResponse } from '@/services/PlansService'
import { format } from 'date-fns'

export default Vue.extend({
  name: 'PlanSettingsInitiatives',
  components: {
    ViewHeader,
    ViewMain,
    HowItWorks,
  },
  data: () => ({
    fileTypes: '.jpg,.jpeg,.png,.gif,.webp' as string,
    topViewContent: {
      title: Channel.CHANNEL_NAME_INITIATIVES,
      infoText:
        'Enter priority Initiatives to view in the plan and connect related Tactics.',
    },
    shouldShowTagList: false,
    selectedCategory: {} as InitiativeType,
    currentOverlayHTML: '' as string,
    currentInitiativesTableSortField: 'initiativeTypeName' as string,
    categoryEditingRows: [],
    newCategoryName: '',
    isExportingInitiatives: false as boolean,
  }),
  created() {
    // Ensure initiatives are fetched when this view is created
    this.$store.dispatch('updateInitiativeSettingsDateRange', {
      newRange: this.$store.getters.initiativeSettingsDateRange,
      shouldFetchInitiatives: true
    })
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    initiativeChannel(): InitiativeChannel {
      return this.$store.getters.currentPlan.initiativeChannel
    },
    categoryOptions(): InitiativeType[] {
      return this.initiativeChannel.sortedInitiativeTypes
    },
    isLeadPlan(): boolean {
      return this.currentPlan.plans.length ? true : false
    },
    exportPlanIntIds(): number[] {
      // Same plans this view fetches initiatives for (fetchInitiativesForCurrentPlanInDateRange), lead plan first
      if (this.currentPlan.isNested) {
        return [this.currentPlan.parentId.intID, this.currentPlan.id.intID]
      }
      return [this.currentPlan.id.intID, ...this.currentPlan.plans.map((planId: ID) => planId.intID)]
    },
    canUserEditPlanProperties(): boolean {
      return this.$store.getters.getPermissionLevelForPlanId(this.currentPlan.id) <=
        Role.LEVEL_PLAN_ADMIN
        ? true
        : false
    },
    canUserManagePlanUsersChannelsSubscriptions(): boolean {
      return this.$store.getters.getPermissionLevelForPlanId(this.currentPlan.id) <=
        Role.LEVEL_CONTRIBUTOR
        ? true
        : false
    },
    dateRangeStart: {
      get(): Date {
        return this.$store.getters.initiativeSettingsDateRange[0]
      },
      set(newDate: Date) {
        newDate.setHours(0, 0, 0, 0)
        this.$store.dispatch('updateInitiativeSettingsDateRange', { newRange: [newDate, this.$store.getters.initiativeSettingsDateRange[1]], shouldFetchInitiatives: true })

        if (this.$store.getters.initiativeSettingsDateRange[0] > this.$store.getters.initiativeSettingsDateRange[1]) {
          const newEndDate = new Date(this.$store.getters.initiativeSettingsDateRange[0].getTime())
          newEndDate.setDate(this.$store.getters.initiativeSettingsDateRange[0].getDate() + 30)
          this.dateRangeEnd = newEndDate
        }
      },
    },
    dateRangeEnd: {
      get(): Date {
        return this.$store.getters.initiativeSettingsDateRange[1]
      },
      set(newDate: Date) {
        newDate.setHours(23, 59, 59, 999)
        this.$store.dispatch('updateInitiativeSettingsDateRange', { newRange: [this.$store.getters.initiativeSettingsDateRange[0], newDate], shouldFetchInitiatives: true })

        if (this.$store.getters.initiativeSettingsDateRange[0] > this.$store.getters.initiativeSettingsDateRange[1]) {
          const newStartDate = new Date(this.$store.getters.initiativeSettingsDateRange[1].getTime())
          newStartDate.setDate(this.$store.getters.initiativeSettingsDateRange[0].getDate() - 30)
          this.dateRangeStart = newStartDate
        }
      },
    },
    filteredInitiatives(): PlanningItem[] {
      const planningItems: PlanningItem[] = []

      // Guard against uninitialized date range
      if (!this.dateRangeStart || !this.dateRangeEnd) {
        return planningItems
      }

      // Get filtered initiatives
      const initiatives = this.$store.getters.currentInitiatives.filter((initiative) =>
        !(
          initiative.endDate < this.dateRangeStart ||
          initiative.startDate > this.dateRangeEnd
        ) && (
          this.selectedCategory &&
            this.selectedCategory.name ? initiative.initiativeTypeName === this.selectedCategory.name : true
        )
      )

      // Add initiatives and their customizations
      initiatives.forEach((initiative: Initiative) => {
        // Add the initiative itself
        planningItems.push(initiative)

        // Add its customizations as separate items
        if (initiative.initiativeCustomizations && initiative.initiativeCustomizations.length) {
          initiative.initiativeCustomizations.forEach((customization: InitiativeCustomization) => {
            // Only include customizations that match the date range
            if (!(customization.endDate < this.dateRangeStart || customization.startDate > this.dateRangeEnd)) {
              planningItems.push(customization)
            }
          })
        }
      })

      return planningItems
    },
    doesPlanHaveObjectives(): boolean {
      return this.$store.getters.currentTags.filter((tag) => tag.isObjective)
        .length
        ? true
        : false
    },
    doesPlanHaveTargetSegments(): boolean {
      return this.$store.getters.currentTags.filter(
        (tag) => tag.isTargetSegment
      ).length
        ? true
        : false
    },
    initiativeTypeNameMonthlyTheme(): string {
      return InitiativeType.INITIATIVE_TYPE_NAME_MONTHLY_THEME
    }
  },
  methods: {
    async onCategoryRowReorder(event) {
      // Update orderIndex values and persist values to API sequentially
      for (let i = 0; i < event.value.length; i++) {
        const type = event.value[i]
        type.orderIndex = i
        try {
          await this.$store.getters.services.initiatives.updateInitiativeType(type)
        } catch (error) {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary:
              'There was an error reordering initiative categories. Please try again.',
          })
          return
        }
      }
    },
    onCategotyRowEditInit() {
      // Init row edit
    },
    onCategotyRowEditSave(event) {
      const newInitiativeTypeName = event.newData.name
      const thisInitiativeType = event.data as InitiativeType

      // TODO: is matching type names necessary with new InitiativeChannel
      // Create an array of other TacticTypes with the same name
      // let matchingNameTacticTypes = [] as InitiativeType[]
      // this.$store.getters.currentPlan.allInitiativeChannels.forEach((channel)=>{
      //   matchingNameTacticTypes = matchingNameTacticTypes.concat(channel.tacticTypes.filter((checkType) => checkType.id.intID !== event.data.id.intID && checkType.name.toLowerCase() === event.data.name.toLowerCase()))
      // })

      // let matchingNameInitiativeTypes = [] as InitiativeType[]
      // matchingNameInitiativeTypes = matchingNameInitiativeTypes.concat(this.initiativeChannel.initiativeTypes.filter((checkType) => checkType.id.intID !== event.data.id.intID && checkType.name.toLowerCase() === event.data.name.toLowerCase()))

      // Update name on initiative type object
      thisInitiativeType.name = newInitiativeTypeName

      // Create array of promises for InitiativeTypes to update
      const allPromises = [
        new Promise((resolve, reject) => {
          this.$store.getters.services.initiatives.updateInitiativeType(thisInitiativeType).then(
            (response) => {
              resolve(response)
            },
            (error) => {
              reject(error)
            }
          )
        })
      ]

      // matchingNameInitiativeTypes.forEach((type)=>{
      //   type.name = newInitiativeTypeName
      //   allPromises.push(
      //     new Promise((resolve, reject) => {
      //       this.$store.getters.services.initiatives.updateInitiativeType(type).then(
      //         (response)=>{
      //           resolve(response)
      //         },
      //         (error)=>{
      //           reject(error)
      //         }
      //       )
      //     })
      //   )
      // })

      Promise.all(allPromises).then((values) => {
        this.$store.dispatch('refreshCurrentPlan')
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.success,
          summary: 'Initiative category updated.',
          life: 3000,
        })
      })
        .catch((error) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary:
              'There was an error updating the initiative category. Please try again.',
          })
        })
    },
    onCategoryDelete(event, category: InitiativeType) {
      this.$confirm.require({
        acceptLabel: 'YES',
        rejectLabel: 'NO',
        target: event.currentTarget,
        message: 'Are you sure you want to delete this Category of Initiatives? All Initiatives within this Category will be deleted along with it.',
        acceptClass: 'btn-delete-accept',
        accept: () => {
          this.$store.getters.services.initiatives.deleteInitiativeType(category.id.intID).then(
            () => {
              this.$store.dispatch('refreshCurrentPlan')

              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.success,
                summary: 'Initiative category deleted.',
                life: 3000,
              })
            },
            (error) => {
              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.error,
                summary:
                  'There was an error deleting the initiative category. Please try again.',
              })
            }
          )
        },
        reject: () => {
          //Delete rejected
        },
      })
    },
    addCategory() {
      if (this.newCategoryName === '') {
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.warn,
          summary: 'Please add a name for the new category.',
          life: 3000,
        })
        return
      }

      const newInitiativeType = new InitiativeType()
      newInitiativeType.name = this.newCategoryName
      newInitiativeType.initiativeChannelId = this.initiativeChannel.id.clone()
      newInitiativeType.orderIndex = this.categoryOptions.length
      newInitiativeType.channelName = this.initiativeChannel.name
      newInitiativeType.abbreviatedPlanName = this.currentPlan.abbreviatedName
      newInitiativeType.creatorId = this.$store.getters.currentUser.id

      this.$store.getters.services.initiatives.createInitiativeType(newInitiativeType).then(
        (newType) => {
          this.newCategoryName = ''
          this.$store.dispatch('refreshCurrentPlan')

          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Initiative category created.',
            life: 3000,
          })
        },
        (error) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary:
              `There was an error creating the initiative category. Please try again. (Error: ${error})`,
          })
        }
      )
    },
    closeModal() {
      this.$emit('complete')
    },
    handleAddInitiative(id) {
      const targetIntID = id.intID ? id.intID : 0
      this.$router.push({
        path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/initiative/${targetIntID}?datetime=${this.dateRangeStart}`,
      })
    },
    getTitleForPlanningItem(planningItem: PlanningItem) {
      let returnString = planningItem.title
      if (planningItem.abbreviatedPlanName && planningItem.abbreviatedPlanName !== '') {
        if (returnString.length > 0) {
          returnString = planningItem.abbreviatedPlanName + '  |  ' + returnString
        } else {
          returnString = planningItem.abbreviatedPlanName
        }
      }
      return returnString
    },
    showPlanningItem: function (planningItem: PlanningItem) {
      // For InitiativeCustomizations, navigate to the parent Initiative
      if(planningItem.isInitiativeCustomization){
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/initiative/${planningItem.initiativeId?.intID}/nested-variant/${planningItem.id.intID}`,
        })
      }else{
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/initiative/${planningItem.id.intID}`,
        })
      }
    },
    getObjectivesForPlanningItem: function (planningItem: PlanningItem): Tag[] {
      return planningItem.tags ? planningItem.tags.filter((tag) => tag.isObjective) : []
    },
    getTargetSegmentsForPlanningItem: function (planningItem: PlanningItem): Tag[] {
      return planningItem.tags ? planningItem.tags.filter((tag) => tag.isTargetSegment) : []
    },
    onClickExportInitiatives() {
      const startDate = format(this.dateRangeStart, 'yyyy-MM-dd')
      const endDate = format(this.dateRangeEnd, 'yyyy-MM-dd')
      this.isExportingInitiatives = true
      this.$store.getters.services.plans
        .exportPlans(
          this.exportPlanIntIds,
          startDate,
          endDate,
          ['initiatives']
        )
        .then(
          (response: PlanExportResponse) => {
            window.open(response.url, '_blank')
          },
          (error) => {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: 'Error exporting plan initiatives.',
              detail: error,
            })
          }
        )
        .finally(() => {
          this.isExportingInitiatives = false
        })
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.p-datatable.initiatives-table {
  .p-datatable-tbody>tr>td {
    .id-value {
      position: absolute;
    }
  }
}

.integration-data-table {
  border: 0.01rem solid black;

  th,
  td {
    border: 0.01rem solid black;
    padding: 0.5rem;
  }
  th {
    @include font-bold;
    font-size: 1.2rem;
  }
}
</style>